import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Save } from "lucide-react";
import { setApiKey as setSessionApiKey, STORAGE_KEYS } from "../utils/storage";

const Settings = () => {
  const [formSettings, setFormSettings] = useState({
    apiKey: "",
    language: "en",
    length: "murmur",
    autoGenerate: false,
    useProductName: true,
    useProductBrand: true,
    start_with: "",
    negative_keywords: "",
    useProductDescription: true,
    useProductSeoKeywords: true
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  // Language options
  const languages = [
    { label: "English", value: "en" },
    { label: "Afrikaans", value: "af" },
    { label: "Albanian", value: "sq" },
    { label: "Amharic", value: "am" },
    { label: "Arabic", value: "ar" },
    { label: "Armenian", value: "hy" },
    { label: "Assamese", value: "as" },
    { label: "Aymara", value: "ay" },
    { label: "Azerbaijani", value: "az" },
    { label: "Bambara", value: "bm" },
    { label: "Basque", value: "eu" },
    { label: "Belarusian", value: "be" },
    { label: "Bengali", value: "bn" },
    { label: "Bhojpuri", value: "bho" },
    { label: "Bosnian", value: "bs" },
    { label: "Bulgarian", value: "bg" },
    { label: "Catalan", value: "ca" },
    { label: "Cebuano", value: "ceb" },
    { label: "Chinese (Simplified)", value: "zh-cn" },
    { label: "Chinese (Traditional)", value: "zh-tw" },
    { label: "Corsican", value: "co" },
    { label: "Croatian", value: "hr" },
    { label: "Czech", value: "cs" },
    { label: "Danish", value: "da" },
    { label: "Dhivehi", value: "dv" },
    { label: "Dogri", value: "doi" },
    { label: "Dutch", value: "nl" },
    { label: "Esperanto", value: "eo" },
    { label: "Estonian", value: "et" },
    { label: "Ewe", value: "ee" },
    { label: "Filipino (Tagalog)", value: "fil" },
    { label: "Finnish", value: "fi" },
    { label: "French", value: "fr" },
    { label: "Frisian", value: "fy" },
    { label: "Galician", value: "gl" },
    { label: "Georgian", value: "ka" },
    { label: "German", value: "de" },
    { label: "Greek", value: "el" },
    { label: "Guarani", value: "gn" },
    { label: "Gujarati", value: "gu" },
    { label: "Haitian Creole", value: "ht" },
    { label: "Hausa", value: "ha" },
    { label: "Hawaiian", value: "haw" },
    { label: "Hebrew", value: "he" },
    { label: "Hindi", value: "hi" },
    { label: "Hmong", value: "hmn" },
    { label: "Hungarian", value: "hu" },
    { label: "Icelandic", value: "is" },
    { label: "Igbo", value: "ig" },
    { label: "Ilocano", value: "ilo" },
    { label: "Indonesian", value: "id" },
    { label: "Irish", value: "ga" },
    { label: "Italian", value: "it" },
    { label: "Japanese", value: "ja" },
    { label: "Javanese", value: "jv" },
    { label: "Kannada", value: "kn" },
    { label: "Kazakh", value: "kk" },
    { label: "Khmer", value: "km" },
    { label: "Kinyarwanda", value: "rw" },
    { label: "Konkani", value: "gom" },
    { label: "Korean", value: "ko" },
    { label: "Krio", value: "kri" },
    { label: "Kurdish", value: "ku" },
    { label: "Kurdish (Sorani)", value: "ckb" },
    { label: "Kyrgyz", value: "ky" },
    { label: "Lao", value: "lo" },
    { label: "Latin", value: "la" },
    { label: "Latvian", value: "lv" },
    { label: "Lingala", value: "ln" },
    { label: "Lithuanian", value: "lt" },
    { label: "Luganda", value: "lg" },
    { label: "Luxembourgish", value: "lb" },
    { label: "Macedonian", value: "mk" },
    { label: "Maithili", value: "mai" },
    { label: "Malagasy", value: "mg" },
    { label: "Malay", value: "ms" },
    { label: "Malayalam", value: "ml" },
    { label: "Maltese", value: "mt" },
    { label: "Maori", value: "mi" },
    { label: "Marathi", value: "mr" },
    { label: "Meiteilon (Manipuri)", value: "mni-mtei" },
    { label: "Mizo", value: "lus" },
    { label: "Mongolian", value: "mn" },
    { label: "Myanmar (Burmese)", value: "my" },
    { label: "Nepali", value: "ne" },
    { label: "Norwegian", value: "no" },
    { label: "Nyanja (Chichewa)", value: "ny" },
    { label: "Odia (Oriya)", value: "or" },
    { label: "Oromo", value: "om" },
    { label: "Pashto", value: "ps" },
    { label: "Persian", value: "fa" },
    { label: "Polish", value: "pl" },
    { label: "Portuguese", value: "pt" },
    { label: "Punjabi", value: "pa" },
    { label: "Quechua", value: "qu" },
    { label: "Romanian", value: "ro" },
    { label: "Russian", value: "ru" },
    { label: "Samoan", value: "sm" },
    { label: "Sanskrit", value: "sa" },
    { label: "ScotsGaelic", value: "gd" },
    { label: "Sepedi", value: "nso" },
    { label: "Serbian", value: "sr" },
    { label: "Sesotho", value: "st" },
    { label: "Shona", value: "sn" },
    { label: "Sindhi", value: "sd" },
    { label: "Sinhala (Sinhalese)", value: "si" },
    { label: "Slovak", value: "sk" },
    { label: "Slovenian", value: "sl" },
    { label: "Somali", value: "so" },
    { label: "Spanish", value: "es" },
    { label: "Sundanese", value: "su" },
    { label: "Swahili", value: "sw" },
    { label: "Swedish", value: "sv" },
    { label: "Tagalog (Filipino)", value: "tl" },
    { label: "Tajik", value: "tg" },
    { label: "Tamil", value: "ta" },
    { label: "Tatar", value: "tt" },
    { label: "Telugu", value: "te" },
    { label: "Thai", value: "th" },
    { label: "Tigrinya", value: "ti" },
    { label: "Tsonga", value: "ts" },
    { label: "Turkish", value: "tr" },
    { label: "Turkmen", value: "tk" },
    { label: "Twi (Akan)", value: "ak" },
    { label: "Ukrainian", value: "uk" },
    { label: "Urdu", value: "ur" },
    { label: "Uyghur", value: "ug" },
    { label: "Uzbek", value: "uz" },
    { label: "Vietnamese", value: "vi" },
    { label: "Welsh", value: "cy" },
    { label: "Xhosa", value: "xh" },
    { label: "Yiddish", value: "yi" },
    { label: "Yoruba", value: "yo" },
    { label: "Zulu", value: "zu" }
  ];

  // Alt text examples
  const altTextExamples = {
    whisper: {
      label: 'Whisper',
      chars: '80 Chars Max',
      example: 'Rain cloaks the silent mountains as mist weaves through pine-covered slopes.',
    },
    murmur: {
      label: 'Murmur',
      chars: '120 Chars Max',
      example: 'Dark clouds embrace the peaks, while rain dances on pine needles and mist curls between ridges.',
    },
    voice: {
      label: 'Voice',
      chars: '200 Chars Max',
      example: 'Thunder echoes through the mountain pass as rain blankets the forested slopes. Pines sway in the wind, and mist snakes through the valleys, cloaking the wilderness in moody silence.',
    },
    echo: {
      label: 'Echo',
      chars: '250 Chars Max',
      example: 'Rain cascades down rugged mountain slopes, drenching pine forests in a steady rhythm. Thunder rolls above as silver mist weaves through the peaks, softening their outlines in a dance of shadow and storm. Nature breathes in calm defiance.',
    },
  };

  // Get store ID from localStorage only
  const getStoreId = () => {
    const storedId = sessionStorage.getItem(STORAGE_KEYS.STORE_ID);
    console.log("Settings - ID from localStorage:", storedId);

    if (storedId) {
      console.log("Settings - Using stored ID:", storedId);
      return storedId;
    }

    console.log("Settings - No store ID found in localStorage!");
    return null;
  };

  // Load settings from backend
  const loadSettings = async () => {
    const storeId = getStoreId();
    if (!storeId) {
      setIsLoading(false);
      return;
    }
    const apiUrl = import.meta.env.VITE_BACKEND_URL;
    try {
      const response = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          setFormSettings(prev => ({ ...prev, ...data.settings }));
        }
      } else {
        // Fallback to localStorage if backend fails
        const savedSettings = localStorage.getItem("alttextify_settings");
        console.log("Settings - Saved settings:", savedSettings);
        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings);
            setFormSettings(prev => ({ ...prev, ...parsed }));
          } catch (error) {
            console.error("Failed to parse saved settings:", error);
          }
        }
      }
    } catch (error) {
      console.error("Failed to load settings from backend:", error);
      // Fallback to localStorage
      const savedSettings = localStorage.getItem("alttextify_settings");
      console.log("Settings - Saved settings:", savedSettings);
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setFormSettings(prev => ({ ...prev, ...parsed }));
        } catch (error) {
          console.error("Failed to parse saved settings:", error);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSettingsChange = (field, value) => {
    setFormSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const storeId = getStoreId();

      if (storeId) {
        // Save to backend
        const apiUrl = import.meta.env.VITE_BACKEND_URL;
        const url = `${apiUrl}/api/settings?id=${storeId}`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formSettings)
        });

        const responseText = await response.text();

        if (response.ok) {
          const data = JSON.parse(responseText);
          console.log("Settings - Save settings response:", data);
          if (data.success) {
            setNotification({
              type: 'success',
              message: 'Settings saved successfully!'
            });
          } else {
            setNotification({
              type: 'danger',
              message: data.message || 'Failed to save settings'
            });
          }
        } else {
          throw new Error(`Failed to save settings to backend: ${response.status} ${responseText}`);
        }
      } else {
        // Fallback to localStorage only
        localStorage.setItem("alttextify_settings", JSON.stringify(formSettings));
        setNotification({
          type: 'success',
          message: 'Settings saved to local storage!'
        });
      }

      // Save API key separately for dashboard access
      if (formSettings.apiKey) {
        localStorage.setItem("alttextify_api_key", formSettings.apiKey);
        setSessionApiKey(formSettings.apiKey);
      }

      // Clear notification after 3 seconds
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error("Save settings error:", error);
      setNotification({
        type: 'error',
        message: 'Failed to save settings. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4 text-center">
                <div className="loading-dots" role="status" style={{ width: '48px', height: '48px', margin: '0 auto' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading settings...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12">
          <h3 className="mb-4 fw-bold" style={{ fontSize: '1.25rem' }}>Settings</h3>
          
          <form onSubmit={handleSaveSettings}>
            <div className="row">
              {/* Left Column */}
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h5 className="card-title fw-bold mb-4">API Configuration</h5>
                    
                    {/* API Key Section */}
                    <div className="mb-4">
                      <label htmlFor="apiKey" className="form-label fw-semibold">API Key</label>
                      <div className="input-group">
                        <input
                          type={showApiKey ? "text" : "password"}
                          className="form-control"
                          id="apiKey"
                          value={formSettings.apiKey}
                          onChange={e => handleSettingsChange('apiKey', e.target.value)}
                          placeholder="Enter your API key"
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          tabIndex={-1}
                          onClick={() => setShowApiKey(v => !v)}
                          aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKey ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      <div className="form-text text-dark mt-1">
                        You can find your API key in the <a href="https://alttextify.net/settings/apikeys" target="_blank" rel="noopener noreferrer">settings page</a>.
                      </div>
                    </div>

                    {/* Language Selection */}
                    <div className="mb-4">
                      <label htmlFor="language" className="form-label fw-semibold">Language</label>
                      <select
                        className="form-select"
                        id="language"
                        value={formSettings.language}
                        onChange={e => handleSettingsChange('language', e.target.value)}
                      >
                        {languages.map(language => (
                          <option key={language.value} value={language.value}>{language.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Alt Text Size */}
                    <div className="mb-4">
                      <label htmlFor="length" className="form-label fw-semibold">Alt Text Size</label>
                      <select
                        className="form-select"
                        id="length"
                        value={formSettings.length}
                        onChange={e => handleSettingsChange('length', e.target.value)}
                      >
                        {Object.entries(altTextExamples).map(([value, { label, chars }]) => (
                          <option key={value} value={value}>{label} ({chars})</option>
                        ))}
                      </select>
                      <div className="alert alert-secondary mt-2 mb-0 p-3" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}>
                        <strong>Example:</strong> {altTextExamples[formSettings.length]?.example}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-4">
                    <h5 className="card-title fw-bold mb-4">Content Preferences</h5>
                    
                    {/* Prefix for alt text */}
                    <div className="mb-4">
                      <label htmlFor="startWith" className="form-label fw-semibold">Prefix for alt text</label>
                      <input
                        type="text"
                        className="form-control"
                        id="startWith"
                        value={formSettings.start_with || ''}
                        onChange={e => handleSettingsChange('start_with', e.target.value)}
                        placeholder="e.g. Premium quality, Limited edition, etc."
                      />
                      <div className="form-text text-dark mt-1">
                        This text will be added at the start of every generated alt text.
                      </div>
                    </div>

                    {/* Negative keywords */}
                    <div className="mb-4">
                      <label htmlFor="negativeKeywords" className="form-label fw-semibold">Negative keywords (comma separated)</label>
                      <input
                        type="text"
                        className="form-control"
                        id="negativeKeywords"
                        value={formSettings.negative_keywords || ''}
                        onChange={e => handleSettingsChange('negative_keywords', e.target.value)}
                        placeholder="e.g. cheap, fake, replica"
                      />
                      <div className="form-text text-dark mt-1">
                        These keywords will be excluded from generated alt text. Separate multiple keywords with commas.
                      </div>
                    </div>

                    {/* Alt Text Generation Options */}
                    <div className="card bg-light border-0">
                      <div className="card-body">
                        <h6 className="card-title fw-bold mb-3">Generation Options</h6>

                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="autoGenerate"
                              checked={formSettings.autoGenerate}
                              onChange={e => handleSettingsChange('autoGenerate', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="autoGenerate">
                              Automatically generate alt text when a product is created or updated
                            </label>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="useProductName"
                              checked={formSettings.useProductName}
                              onChange={e => handleSettingsChange('useProductName', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="useProductName">
                              Use Product Name in generated alt text
                            </label>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="useProductBrand"
                              checked={formSettings.useProductBrand}
                              onChange={e => handleSettingsChange('useProductBrand', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="useProductBrand">
                              Use Product Brand in generated alt text
                            </label>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="useProductDescription"
                              checked={!!formSettings.useProductDescription}
                              onChange={e => handleSettingsChange('useProductDescription', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="useProductDescription">
                              Use product description for alt text generation
                            </label>
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="useProductSeoKeywords"
                              checked={!!formSettings.useProductSeoKeywords}
                              onChange={e => handleSettingsChange('useProductSeoKeywords', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="useProductSeoKeywords">
                              Use product SEO keywords in generated alt text
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notification and Save Button - Full Width */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    {notification && (
                      <div className={`alert alert-${notification.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
                        {notification.message}
                        <button type="button" className="btn-close" onClick={() => setNotification(null)} aria-label="Close"></button>
                      </div>
                    )}
                    
                    {/* Save Button */}
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={isSaving}
                    >
                                        {isSaving ? (
                    <>
                      <span className="loading-dots me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                        <>
                          <Save size={20} className="me-2" />
                          Save Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings; 